import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { ItemImageArea } from 'utoniq-ui'

import {
  DeliveryItemBoxFragment,
  DeliveryStatus,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useTranslation } from '@/core'
import { DateTime } from '@/core/entities'

import { Status } from '../Status'

export type DeliveryManagementBoxProps = {
  delivery: DeliveryItemBoxFragment
  hideSerial?: boolean
  onApply: (delivery: DeliveryItemBoxFragment) => void
}

export const DeliveryManagementBox = ({
  delivery,
  onApply,
  hideSerial,
}: DeliveryManagementBoxProps) => {
  const { t } = useTranslation('deliveryManagement')
  const activeStep = useMemo<number>(() => {
    switch (delivery.status) {
      case DeliveryStatus.Shipped:
        return 1
      default:
        return 0
    }
  }, [delivery.status])

  const isExpire = useMemo(() => {
    return (
      !!delivery.item.receptionPeriod?.formattedEndedAt &&
      delivery.item.receptionPeriod?.formattedEndedAt < DateTime.now()
    )
  }, [delivery.item.receptionPeriod?.formattedEndedAt])

  return (
    <Card sx={{ width: '100%' }}>
      <CardContent>
        <Stack direction="row" flex={1} spacing={4}>
          <Stack flex={1}>
            <Typography variant="subtitle1">{delivery.item.title}</Typography>
            {!!delivery.itemSerial?.sku?.unitName && (
              <Typography variant="body2">
                {delivery.itemSerial?.sku?.unitName}
              </Typography>
            )}
            {/**
             * O-MENZのアプリは、シリアル非表示設定であっても、自分のアイテムのシリアル番号は分かるにする
             * https://app.clickup.com/t/908127/UT-11803
             */}
            {(!hideSerial || config.applicationKey === 'omenz') && (
              <Typography color="text.secondary" variant="overline">
                {delivery.itemSerial?.formattedSerialNumber ?? ''}
              </Typography>
            )}
            <Typography variant="caption" sx={{ pt: 1, mb: 3 }}>
              {delivery.item.description}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Typography variant="overline" color="text.secondary">
                {t('applicationDeadLine')}
              </Typography>
              <Typography variant="overline">
                {delivery.item.receptionPeriod?.formattedEndedAt}
              </Typography>
            </Stack>
          </Stack>
          <ItemImageArea
            size="small"
            src={delivery.item.thumbnail.front?.smallUri ?? ''}
          />
        </Stack>
        {delivery.formattedAppliedAt ? (
          <>
            <Status activeStep={activeStep} />
            <Box>
              <Typography variant="subtitle1" color="text.secondary">
                {t('applicationDate')}
              </Typography>
              <Typography variant="body2" sx={{ pt: 1 }}>
                {delivery.formattedAppliedAt}
              </Typography>
            </Box>
            <Box sx={{ pt: 4 }}>
              <Typography variant="subtitle1" color="text.secondary">
                {t('shippingAddress')}
              </Typography>
              <Typography variant="body2" sx={{ pt: 1 }}>
                {delivery.deliveryInfo?.address?.fullAddress}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Stack sx={{ pt: 5 }} justifyContent="center" direction="row">
              <Button
                disabled={isExpire}
                size="small"
                sx={{ minWidth: 130 }}
                onClick={() => onApply(delivery)}
              >
                {t('btnApply')}
              </Button>
            </Stack>
            {!isExpire && (
              <Stack alignItems={'center'} sx={{ mt: 2 }}>
                <Typography variant="caption">{t('note')}</Typography>
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
